<template>
  <CRow class="">
    <CCol col="12" class="mb-2">
      <hr>
    </CCol>
    <CCol lg="5">
      <label class="mb-1 big" v-translate translate-context="event_wizard.label">Upload watermark</label>
      <p class="text-help-dark" v-translate translate-context="event_wizard.subtitle">
        Enter your watermark in the dedicated field to add a semi-transparent overlay to your event photos
      </p>
    </CCol>
    <CCol lg="7">
      <PUploader
          :params="uploadParams.copyright"
          :get_intent="get_upload_intent"
          :value.sync="form.logo"
          :help_text="$pgettext('uploader.help','Allowed formats: .jpeg .png')"
          :resize="{max_width: 2000, max_height: 2000}"
          ref="logoUploader"
          @uploaded="newLogoUploaded"
      />
    </CCol>
    <CCol col="12" class="mt-3 mb-2">
      <hr>
    </CCol>
    <CCol lg="5">
      <label class="mb-1 big" v-translate translate-context="event_wizard.label">Watermark size</label>
    </CCol>
    <CCol lg="7">
      <div class="btn-group-squared btn-group-squared__logo_sizes btn-group-toggle">
        <label class="btn btn-outline-secondary btn-toggle mb-2" v-for="(size, index) in logoSizes" :key="index"
               :class="{'active': size.value === form.size}">
          <input type="radio" name="logo-size"
                 :id="`option-${index}`"
                 v-model="form.size"
                 :value="size.value"
                 :checked="size.value === form.size"
                 @change="save">
          <CIcon size="custom-size" :width="size.iconSize" :height="size.iconSize" name="cipSquare"/>
        </label>
      </div>
    </CCol>
    <CCol col="12" class="mt-3 mb-2">
      <hr>
    </CCol>

    <CCol lg="5">
      <label class="mb-1 big" v-translate translate-context="event_wizard.label">Watermark opacity</label>
    </CCol>
    <CCol lg="7">
      <input type="range"
             min="0"
             max="100"
             v-model="form.bg_opacity"
             @input="save"
             class="form-range"/>
      <div class="text-muted mt-1">{{ form.bg_opacity }}%</div>
    </CCol>


  </CRow>
</template>

<script>
import {upload} from "@/domain/eventWizard/upload";
import {DELETE_KEY, isUploadIntent} from "@/domain/core/components/uploader";
import PUploader from "@/domain/core/components/PUploader.vue";
import {cropperTypes} from "@/domain/core/constant/cropper";

const logoSizes = [
  {
    iconSize: '8',
    value: 'small'
  },
  {
    iconSize: '18',
    value: 'medium'
  },
  {
    iconSize: '28',
    value: 'large'
  },
  {
    iconSize: '44',
    value: 'original'
  }
]


export default {
  name: "WatermarkLogoEditor",
  components: {PUploader},
  props: {
    images: {
      type: [Object, Array],
      required: false
    },
    get_upload_intent: {
      type: Function,
      required: true
    },
    get_upload_intent_public_url: {
      type: Function,
      required: true,
    },
    advanced: {
      type: Boolean,
      required: true,
    }
  },
  emits: ['save', 'updatePreview'],
  computed: {
    cropperTypes() {
      return cropperTypes
    },
    uploadParams() {
      return upload
    },
    logoSizes() {
      if (this.advanced)
        return logoSizes
      return logoSizes.filter(s => s.value !== 'original')
    },

  },
  data() {
    return {
      form: {
        logo: null,
        position: 'top-left',
        size: 'medium',
        bg_color: null,
        bg_opacity: 100,
        tags: []
      },
      logo_url: null,
      default: {
        position: 'top-left',
        size: 'medium',
        bg_color: null,
        bg_opacity: 100,
        tags: []
      }
    }
  },
  watch: {
    images: function () {
      this.init()
    }
  },

  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.logo_url = ''
      if (!this.images.length) {
        this.form = {...this.default}
        this.previewFieldUpdated()
        return
      }

      this.form = {...this.default, ...this.images[0]}
      if (this.form.logo && this.form.logo !== DELETE_KEY)
        this.logo_url = this.form.logo
      this.previewFieldUpdated()
    },
    async newLogoUploaded() {
      await this.$nextTick()
      if (this.form.logo === DELETE_KEY)
        this.logo_url = ''
      else {
        this.logo_url = await this.get_upload_intent_public_url(this.form.logo)
      }
      this.save()
    },

    save() {
      this.previewFieldUpdated()
      this.$emit('save', [
        {...this.form}
      ])
    },
    async previewFieldUpdated() {
      if (!this.logo_url) return this.$emit('updatePreview', [])

      // Get logo dimensions every time
      if (this.form.size === 'original') {
        await this.$nextTick() // Wait for PUploader to process existing file
        this.logoImageDimensions = this.$refs.logoUploader ? await this.$refs.logoUploader.getImageSizes() : null

        // If size is 'original', show only one preview with actual image dimensions
        if (this.logoImageDimensions) {
          const isLandscape = this.logoImageDimensions.width > this.logoImageDimensions.height
          this.$emit('updatePreview', [{
            key: isLandscape ? 'landscape' : 'portrait',
            icon: isLandscape ? 'landscape' : 'portrait',
            width: this.logoImageDimensions.width,
            height: this.logoImageDimensions.height,
            logo_intent: isUploadIntent(this.form.logo) ? this.form.logo : null,
            logo_id: isUploadIntent(this.form.logo) ? null : this.form.id,
            position: this.form.position,
            size: this.form.size,
            bg_color: this.form.bg_color,
            bg_opacity: this.form.bg_opacity,
          }])
          return
        }
      }

      // Default behavior: show both landscape and portrait previews
      this.$emit('updatePreview', [{
        key: 'landscape',
        icon: 'landscape',
        width: 1620,
        height: 1080,
        logo_intent: isUploadIntent(this.form.logo) ? this.form.logo : null,
        logo_id: isUploadIntent(this.form.logo) ? null : this.form.id,
        position: this.form.position,
        size: this.form.size,
        bg_color: this.form.bg_color,
        bg_opacity: this.form.bg_opacity,
      }, {
        key: 'portrait',
        icon: 'portrait',
        width: 1080,
        height: 1620,
        logo_intent: isUploadIntent(this.form.logo) ? this.form.logo : null,
        logo_id: isUploadIntent(this.form.logo) ? null : this.form.id,
        position: this.form.position,
        size: this.form.size,
        bg_color: this.form.bg_color,
        bg_opacity: this.form.bg_opacity,
      }])
    },
  }
}
</script>